/**
 * Main Cart File Upload Previews
 * Replaces default variant images with file upload preview URLs
 * for cart items that have custom artwork uploaded
 */

(function() {
  'use strict';

  const PREFIX = '[MainCart-FileUpload]';
  const DEBUG = window.PITCHPRINT_DEBUG || false;
  const log = (...args) => DEBUG && console.log(PREFIX, ...args);

  // ALWAYS log initialization
  console.log('[MainCart-FileUpload] Script loaded');

  // =====================================
  // FILE UPLOAD DETECTION
  // =====================================
  const isFileUploadItem = (lineItem) => {
    // Check if item has file upload properties
    const hasUploadId = lineItem.properties?.['_file_upload_id'];
    const hasFrontPreview = lineItem.properties?.['_file_upload_front_preview'];
    const hasBackPreview = lineItem.properties?.['_file_upload_back_preview'];
    const hasFrontUrl = lineItem.properties?.['_front_file_url'];
    const hasBackUrl = lineItem.properties?.['_back_file_url'];

    console.log('[MainCart-FileUpload] Checking if file upload item:', {
      hasUploadId: !!hasUploadId,
      hasFrontPreview: !!hasFrontPreview,
      hasBackPreview: !!hasBackPreview,
      hasFrontUrl: !!hasFrontUrl,
      hasBackUrl: !!hasBackUrl,
      productTitle: lineItem.product_title
    });

    return !!(hasUploadId || hasFrontPreview || hasBackPreview || hasFrontUrl || hasBackUrl);
  };

  const getFileUploadUrls = (lineItem) => {
    // Use hidden preview URLs, not the visible filename properties
    const frontUrl = lineItem.properties?.['_file_upload_front_preview'] || lineItem.properties?.['_front_file_url'];
    const backUrl = lineItem.properties?.['_file_upload_back_preview'] || lineItem.properties?.['_back_file_url'];
    const sides = lineItem.properties?.['_file_upload_sides'] || 'single';

    console.log('[MainCart-FileUpload] Extracted URLs:', {
      frontUrl,
      backUrl,
      sides,
      allProperties: lineItem.properties
    });

    return {
      front: frontUrl || null,
      back: backUrl || null,
      sides: sides,
      isDoubleSided: sides === 'double' && !!backUrl
    };
  };

  // =====================================
  // PREVIEW IMAGE REPLACEMENT
  // =====================================
  const replaceCartItemImage = (cartItemElement, urls, lineItem) => {
    console.log('[MainCart-FileUpload] 🖼️ replaceCartItemImage() called with urls:', urls);
    log('🖼️ Replacing cart item image with file upload preview');

    // Find the image element
    const imageElement = cartItemElement.querySelector('.cart-item__image');
    console.log('[MainCart-FileUpload] Image element:', imageElement);
    if (!imageElement) {
      console.log('[MainCart-FileUpload] ❌ No .cart-item__image found');
      log('❌ No image element found');
      return;
    }

    // Find the image container
    const imageContainer = cartItemElement.querySelector('.cart-item__image-container');
    console.log('[MainCart-FileUpload] Image container:', imageContainer);
    if (!imageContainer) {
      console.log('[MainCart-FileUpload] ❌ No .cart-item__image-container found');
      log('❌ No image container found');
      return;
    }

    // Check if we already added a preview
    if (imageContainer.querySelector('.pitchprint-preview-container')) {
      console.log('[MainCart-FileUpload] ✅ Preview already exists, skipping');
      log('✅ Preview already exists, skipping');
      return;
    }

    // Get shape from properties
    const shape = lineItem.properties?.['_file_upload_shape'] || '';
    const isCircle = shape.toLowerCase().includes('circle');

    // Create preview container matching mini-cart structure
    const previewContainer = document.createElement('div');
    previewContainer.className = 'pitchprint-preview-container loaded';

    // Add shape class if circle
    if (isCircle) {
      previewContainer.classList.add('circle-shape');
    }

    // Add single-sided class if not double-sided
    if (!urls.isDoubleSided) {
      previewContainer.classList.add('single-sided');
    }

    // Dimensions are set via CSS in main-cart-items.liquid (120x120px)
    // No inline styles needed - let CSS handle it
    console.log('[MainCart-FileUpload] Preview container dimensions:', {
      width: '120px (via CSS)',
      height: '120px (via CSS)',
      isCircle,
      isDoubleSided: urls.isDoubleSided
    });

    // Create preview cards container
    const previewCards = document.createElement('div');
    previewCards.className = 'preview-cards';

    if (urls.isDoubleSided) {
      // Double-sided: Create front and back cards matching mini-cart structure
      console.log('[MainCart-FileUpload] 🎨 Creating double-sided preview with card structure');
      log('🎨 Creating double-sided preview with card structure');

      // Front card
      const frontCard = document.createElement('div');
      frontCard.className = 'preview-card preview-card-front front';
      const frontImg = document.createElement('img');
      frontImg.src = urls.front;
      frontImg.alt = 'Design Side 1';
      frontImg.style.cssText = 'width: 100%; height: 100%; object-fit: cover; display: block;';
      frontCard.appendChild(frontImg);

      // Back card
      const backCard = document.createElement('div');
      backCard.className = 'preview-card preview-card-back back';
      const backImg = document.createElement('img');
      backImg.src = urls.back;
      backImg.alt = 'Design Side 2';
      backImg.style.cssText = 'width: 100%; height: 100%; object-fit: cover; display: block;';
      backCard.appendChild(backImg);

      // Assemble structure
      previewCards.appendChild(frontCard);
      previewCards.appendChild(backCard);
      previewContainer.appendChild(previewCards);

      // Setup toggle functionality matching mini-cart
      let currentSide = 1;
      const toggleSide = () => {
        if (currentSide === 1) {
          frontCard.classList.remove('front');
          frontCard.classList.add('back');
          backCard.classList.remove('back');
          backCard.classList.add('front');
          currentSide = 2;
        } else {
          frontCard.classList.remove('back');
          frontCard.classList.add('front');
          backCard.classList.remove('front');
          backCard.classList.add('back');
          currentSide = 1;
        }

        // Add switching animation class
        previewContainer.classList.add('switching');
        setTimeout(() => {
          previewContainer.classList.remove('switching');
        }, 400);
      };

      // Make entire container clickable for toggle
      previewContainer.style.cursor = 'pointer';
      previewContainer.addEventListener('click', toggleSide);

      console.log('[MainCart-FileUpload] Created double-sided cards:', {
        frontSrc: frontImg.src,
        backSrc: backImg.src
      });
    } else {
      // Single-sided: Create single card matching mini-cart structure
      console.log('[MainCart-FileUpload] 🎨 Creating single-sided preview with card structure');
      log('🎨 Creating single-sided preview with card structure');

      const frontCard = document.createElement('div');
      frontCard.className = 'preview-card preview-card-front front';
      const frontImg = document.createElement('img');
      frontImg.src = urls.front;
      frontImg.alt = 'Custom Artwork';
      frontImg.style.cssText = 'width: 100%; height: 100%; object-fit: cover; display: block;';
      frontCard.appendChild(frontImg);

      previewCards.appendChild(frontCard);
      previewContainer.appendChild(previewCards);

      console.log('[MainCart-FileUpload] Created single-sided card:', { frontSrc: frontImg.src });
    }

    // Replace the default image with our preview
    imageElement.style.display = 'none';
    imageContainer.appendChild(previewContainer);

    console.log('[MainCart-FileUpload] ✅ Preview container appended to DOM');
    console.log('[MainCart-FileUpload] Preview structure:', {
      hasPreviewCards: !!previewContainer.querySelector('.preview-cards'),
      cardCount: previewContainer.querySelectorAll('.preview-card').length
    });
    log('✅ File upload preview added to main cart item with card-based structure');
  };

  // =====================================
  // CART PROCESSING
  // =====================================
  const processMainCart = async () => {
    console.log('[MainCart-FileUpload] processMainCart() called');
    log('🔄 Processing main cart for file upload previews');

    // Get cart data from Shopify API
    let cartData;
    try {
      const response = await fetch('/cart.js');
      cartData = await response.json();
      log('📦 Cart data fetched:', cartData.items.length, 'items');
    } catch (error) {
      log('❌ Failed to fetch cart data:', error);
      return;
    }

    // Get all cart item elements
    const cartItems = document.querySelectorAll('.cart-item');
    console.log('[MainCart-FileUpload] Found', cartItems.length, 'cart items in DOM');
    log('🔍 Found', cartItems.length, 'cart items in DOM');

    if (cartItems.length !== cartData.items.length) {
      console.log('[MainCart-FileUpload] ⚠️ Cart item count mismatch! DOM:', cartItems.length, 'API:', cartData.items.length);
      log('⚠️ Cart item count mismatch! DOM:', cartItems.length, 'API:', cartData.items.length);
    }

    // Process each cart item
    cartItems.forEach((cartItemElement, index) => {
      const lineItem = cartData.items[index];

      if (!lineItem) {
        console.log(`[MainCart-FileUpload] ⚠️ No line item data for cart item ${index + 1}`);
        log(`⚠️ No line item data for cart item ${index + 1}`);
        return;
      }

      console.log(`[MainCart-FileUpload] Processing cart item ${index + 1}:`, lineItem.product_title);
      log(`📝 Processing cart item ${index + 1}:`, lineItem.product_title);

      // Check if this is a file upload item
      if (isFileUploadItem(lineItem)) {
        console.log(`[MainCart-FileUpload] ✅ File upload item detected: ${lineItem.product_title}`);
        log(`✅ File upload item detected: ${lineItem.product_title}`);

        const urls = getFileUploadUrls(lineItem);
        console.log('[MainCart-FileUpload] File upload URLs:', urls);
        log('🔗 File upload URLs:', urls);

        if (urls.front) {
          console.log('[MainCart-FileUpload] Calling replaceCartItemImage with urls:', urls);
          replaceCartItemImage(cartItemElement, urls, lineItem);
        } else {
          console.log('[MainCart-FileUpload] ⚠️ No front URL found');
          log('⚠️ No front URL found for file upload item');
        }
      } else {
        console.log(`[MainCart-FileUpload] Not a file upload item: ${lineItem.product_title}`);
        log(`ℹ️ Not a file upload item: ${lineItem.product_title}`);
      }
    });

    console.log('[MainCart-FileUpload] ✅ Main cart processing complete');
    log('✅ Main cart processing complete');
  };

  // =====================================
  // INITIALIZATION
  // =====================================
  const init = () => {
    console.log('[MainCart-FileUpload] init() called');
    log('🚀 Initializing main cart file upload previews');

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', processMainCart);
    } else {
      processMainCart();
    }

    // Re-process when cart updates
    document.addEventListener('cart:updated', () => {
      log('🔄 Cart updated event detected, re-processing');
      setTimeout(processMainCart, 100);
    });

    log('✅ Main cart file upload preview system ready');
  };

  // Start the system
  init();
})();
